import { z } from 'zod';

/**
 * The phonebook: a client's own contacts and companies (docs/32).
 *
 * The client's only. The API refuses anybody above the account, keys and impersonation
 * (requireClient), and the database refuses the same (migration 0072).
 */

/** Which book a contact is in: the account's, a department's, or one extension's own. */
export const PHONEBOOKS = ['account', 'department', 'personal'] as const;
export type Phonebook = (typeof PHONEBOOKS)[number];

export const PHONEBOOK_NUMBER_KINDS = ['work', 'mobile', 'home', 'other'] as const;
export type PhonebookNumberKind = (typeof PHONEBOOK_NUMBER_KINDS)[number];

/** How every phonebook number is kept: digits, with a + in front for an international one. */
export const PHONEBOOK_NUMBER_PATTERN = /^\+?[0-9]{2,20}$/;

/**
 * A number as somebody typed or exported it, as kept: spaces, dashes, dots and brackets
 * dropped, and 00 in front read as +. Null when what is left is not a number.
 */
export function normalisePhonebookNumber(raw: string): string | null {
  let n = raw.trim().replace(/[\s\-.()/]/g, '');
  if (n.startsWith('00')) n = `+${n.slice(2)}`;
  return PHONEBOOK_NUMBER_PATTERN.test(n) ? n : null;
}

const text = (max: number) => z.string().trim().max(max).nullable().optional()
  .transform((v) => (v ? v : null));

const number = z.string().transform((v, issue) => {
  const n = normalisePhonebookNumber(v);
  if (n === null) {
    issue.addIssue({ code: z.ZodIssueCode.custom, message: 'Use digits, with a + in front for an international number.' });
    return z.NEVER;
  }
  return n;
});

export const PhonebookCompanyInputSchema = z.object({
  name: z.string().trim().min(1, 'Give the company a name.').max(120),
  phone: z.string().nullable().optional().transform((v, issue) => {
    if (!v || !v.trim()) return null;
    const n = normalisePhonebookNumber(v);
    if (n === null) {
      issue.addIssue({ code: z.ZodIssueCode.custom, message: 'Use digits, with a + in front for an international number.' });
      return z.NEVER;
    }
    return n;
  }),
  email: text(254),
  website: text(200),
  notes: text(2000),
}).strict();
export type PhonebookCompanyInput = z.input<typeof PhonebookCompanyInputSchema>;

export const PhonebookNumberSchema = z.object({
  kind: z.enum(PHONEBOOK_NUMBER_KINDS),
  number,
});
export type PhonebookNumber = z.infer<typeof PhonebookNumberSchema>;

export const PhonebookContactInputSchema = z.object({
  book: z.enum(PHONEBOOKS),
  department_id: z.string().uuid().nullable().optional().transform((v) => v ?? null),
  extension_id: z.string().uuid().nullable().optional().transform((v) => v ?? null),
  company_id: z.string().uuid().nullable().optional().transform((v) => v ?? null),
  first_name: text(80),
  last_name: text(80),
  job_title: text(80),
  email: text(254),
  notes: text(2000),
  numbers: z.array(PhonebookNumberSchema).max(8).default([]),
}).strict().superRefine((v, issue) => {
  if (!v.first_name && !v.last_name && !v.company_id) {
    issue.addIssue({ code: z.ZodIssueCode.custom, path: ['first_name'], message: 'Give a name, or choose a company.' });
  }
  if (v.book === 'department' && !v.department_id) {
    issue.addIssue({ code: z.ZodIssueCode.custom, path: ['department_id'], message: 'Choose the department.' });
  }
  if (v.book === 'personal' && !v.extension_id) {
    issue.addIssue({ code: z.ZodIssueCode.custom, path: ['extension_id'], message: 'Choose whose contacts these are.' });
  }
});
export type PhonebookContactInput = z.input<typeof PhonebookContactInputSchema>;
export type PhonebookContactValues = z.infer<typeof PhonebookContactInputSchema>;

export interface PhonebookCompany {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  notes: string | null;
  /** Contacts at it, in the books the caller can see. */
  contacts: number;
  updated_at: string;
}

export interface PhonebookContact {
  id: string;
  book: Phonebook;
  department_id: string | null;
  extension_id: string | null;
  company_id: string | null;
  company_name: string | null;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  email: string | null;
  notes: string | null;
  numbers: PhonebookNumber[];
  updated_at: string;
}

/** What the caller may see and change, for choosing a book. */
export interface PhonebookAccess {
  /** The account and department books, and the companies. */
  can_write: boolean;
  departments: Array<{ id: string; name: string }>;
  /** The extensions whose own contacts the caller keeps: their own. */
  personal: Array<{ id: string; number: string; name: string }>;
}

/** How a contact reads in a list and on a phone: the person, else the company. */
export function contactName(c: Pick<PhonebookContact, 'first_name' | 'last_name' | 'company_name'>): string {
  const person = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return person || c.company_name || '';
}

/**
 * What a number turned out to be, for a call ringing now or a row of call history.
 *
 * Deliberately small: a name, who they are with, and which book it came from. A call screen is
 * not a contact card, and the contact is one click away in the phonebook if somebody wants the
 * rest of it.
 */
export interface PhonebookMatch {
  contact_id: string;
  name: string;
  company: string | null;
  book: Phonebook;
  kind: PhonebookNumberKind;
}
